import React, { useState, useEffect } from 'react';
import { Package, ArrowRight, ArrowLeft, Building, Warehouse, X, Search, AlertCircle, Calendar, ArrowUpDown, FileSpreadsheet } from 'lucide-react';
import axios from 'axios';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

import API_BASE_URL from "../config";

const InventoryDistribution = () => {
  const [poList, setPOList] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poHeader, setPOHeader] = useState(null);
  const [poDetails, setPODetails] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [prHeaders, setPRHeaders] = useState([]);
  const [shippingInfo, setShippingInfo] = useState([]);
  const [shippingLocations, setShippingLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isUpdatingLocation, setIsUpdatingLocation] = useState(false);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');
  const [shipToFilter, setShipToFilter] = useState('');

  const [destinationInfo, setDestinationInfo] = useState(null);

  const [unassignedItems, setUnassignedItems] = useState([]);
  const [assetItems, setAssetItems] = useState([]);
  const [wmsItems, setWMSItems] = useState([]);

  const [selectedUnassigned, setSelectedUnassigned] = useState([]);
  const [selectedAssetItems, setSelectedAssetItems] = useState([]);
  const [selectedWMSItems, setSelectedWMSItems] = useState([]);

  useEffect(() => {
    fetchPOList();
    fetchDestinations();
    fetchPRHeaders();
    fetchShippingLocations();
  }, []);

  const fetchPOList = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}api/T_PO_Header`);

      if (!res.ok) {
        throw new Error(`API Error: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();
      const approvedPOs = Array.isArray(data)
        ? data.filter(po => po.status === 2 && po.receivingStatus === "Pending")
        : [];

      setPOList(approvedPOs);
    } catch (error) {
      console.error('Error fetching PO list:', error);
      alert('Failed to load PO list!\n\n' + error.message);
      setPOList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDestinations = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/InventoryDestination`);
      const data = await res.json();
      setDestinations(data.filter(d => d.isActive));
    } catch (error) {
      console.error('Error fetching destinations:', error);
    }
  };

  const fetchPRHeaders = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/PR_Header`);
      const data = await res.json();
      setPRHeaders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching PR headers:', error);
    }
  };

  const fetchShippingLocations = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}api/ShippingInfo`);
      const activeLocations = res.data.filter(info => info.isActive === 1);
      setShippingInfo(activeLocations);
      setShippingLocations(activeLocations.map(info => info.shipTo_Location).filter(Boolean));
    } catch (error) {
      console.error('Error fetching shipping locations:', error);
      setShippingInfo([]);
      setShippingLocations([]);
    }
  };

  const handleSelectPO = async (po) => {
    try {
      setLoading(true);
      setSelectedPO(po);
      setPOHeader(po);

      const matchingPR = prHeaders.find(pr => pr.prNo === po.inventoryDestinationPRId);

      if (!matchingPR) {
        alert('No matching PR found for this PO!');
        setSelectedPO(null);
        setLoading(false);
        return;
      }

      const destination = destinations.find(d => d.destinationId === matchingPR.inventoryDestinationId);

      if (!destination) {
        alert('Destination not found!');
        setSelectedPO(null);
        setLoading(false);
        return;
      }

      if (!destination.view) {
        alert('This destination is not available for distribution (view is disabled)!');
        setSelectedPO(null);
        setLoading(false);
        return;
      }

      setDestinationInfo(destination);

      const detailsRes = await fetch(`${API_BASE_URL}api/T_Po_Details/POHeader/${po.id}`);

      if (!detailsRes.ok) {
        throw new Error(`Failed to fetch PO details: ${detailsRes.status}`);
      }

      const detailsData = await detailsRes.json();
      setPODetails(Array.isArray(detailsData) ? detailsData : [detailsData]);

      const items = (Array.isArray(detailsData) ? detailsData : [detailsData]).map((detail, index) => ({
        id: detail.id || `item-${index}`,
        itemDescription: detail.itemDescription || 'No description',
        qty: parseFloat(detail.qty) || 0,
        uom: detail.uom || 'pcs',
        unitPrice: parseFloat(detail.unitPrice) || 0,
        poDetailId: detail.id || 0,
        remainingQty: parseFloat(detail.qty) || 0,
        originalQty: parseFloat(detail.qty) || 0,
        fullDetail: detail
      }));

      if (destination.asset && !destination.wms && !destination.both) {
        setUnassignedItems([]);
        setAssetItems(items);
        setWMSItems([]);
      } else if (destination.wms && !destination.asset && !destination.both) {
        setUnassignedItems([]);
        setAssetItems([]);
        setWMSItems(items);
      } else if (destination.both) {
        setUnassignedItems(items);
        setAssetItems([]);
        setWMSItems([]);
      } else {
        setUnassignedItems(items);
        setAssetItems([]);
        setWMSItems([]);
      }

      setSelectedUnassigned([]);
      setSelectedAssetItems([]);
      setSelectedWMSItems([]);

    } catch (error) {
      console.error('Error loading PO details:', error);
      alert('Failed to load PO details: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSelect = async (locationName) => {
    if (!locationName) return;

    const selected = shippingInfo.find(
      info => info.shipTo_Location === locationName
    );

    if (!selected) {
      Swal.fire({
        icon: 'error',
        title: 'Location not found',
        text: 'Location info not found!',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Update Shipping Location?',
      html: `
        <div style="text-align:left">
          <b>Location:</b> ${locationName}<br/>
          <b>Address:</b> ${selected.shipTo_Address1 || ''} ${selected.shipTo_Address2 || ''}<br/>
          <b>Contact:</b> ${selected.shipTo_ContactName || ''} - ${selected.shipTo_ContactNo || ''}
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, update it',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
    });

    if (!result.isConfirmed) return;

    try {
      setIsUpdatingLocation(true);

      const updatePayload = {
        ...poHeader,
        shipTo_Location: locationName,
        shipTo_Address1: selected.shipTo_Address1 || "",
        shipTo_Address2: selected.shipTo_Address2 || "",
        shipTo_ContactPerson: selected.shipTo_ContactName || "",
        shipTo_ContactNo: selected.shipTo_ContactNo || ""
      };

      const response = await fetch(
        `${API_BASE_URL}api/T_PO_Header/${poHeader.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatePayload),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update location');
      }

      setPOHeader(updatePayload);

      Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: 'Location updated successfully.',
        timer: 1500,
        showConfirmButton: false,
      });

    } catch (error) {
      console.error('Error updating location:', error);

      Swal.fire({
        icon: 'error',
        title: 'Update failed',
        text: error.message,
      });
    } finally {
      setIsUpdatingLocation(false);
    }
  };

  const handlePrintToUsedInventory = async () => {
    try {
      const allItems = [...assetItems, ...wmsItems];

      if (allItems.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No Items',
          text: 'No items found in Asset or WMS inventory to print!',
        });
        return;
      }

      // Update print status via API
      try {
        const printResponse = await axios.post(
          `${API_BASE_URL}api/T_PO_Header/update-print/${poHeader.id}`
        );

        if (printResponse.data) {
          // Update local state with new print info
          setPOHeader(prev => ({
            ...prev,
            isPOPrinted: printResponse.data.isPOPrinted,
            reprintCount: printResponse.data.reprintCount
          }));
        }
      } catch (printError) {
        console.error('Error updating print status:', printError);
        // Continue with Excel generation even if print status update fails
      }

      const wb = XLSX.utils.book_new();

      const commonPageSetup = {
        orientation: 'landscape',
        fitToWidth: 1,
        fitToHeight: false,
      };

      const commonMargins = {
        left: 0.5,
        right: 0.5,
        top: 0.75,
        bottom: 0.75,
        header: 0.3,
        footer: 0.3,
      };

      // ================= SHEET 1: PO HEADER (TABLE STYLE) =================
      const headerData = [
        {
          'PO Number': poHeader?.poNumber || 'N/A',
          'PR Reference': poHeader?.inventoryDestinationPRId || 'N/A',
          'PO Created Date': poHeader?.poCreatedDate
            ? new Date(poHeader.poCreatedDate).toLocaleDateString()
            : 'N/A',
          'Delivery Date': poHeader?.deliveryDate
            ? new Date(poHeader.deliveryDate).toLocaleDateString()
            : 'N/A',
          'Payment Terms': poHeader?.paymentTerms || 'N/A',
          'Status': poHeader?.receivingStatus || 'N/A',
          'Ship To Location': poHeader?.shipTo_Location || 'N/A',
          'Ship To Address':
            `${poHeader?.shipTo_Address1 || ''} ${poHeader?.shipTo_Address2 || ''}`.trim() || 'N/A',
          'Contact Person': poHeader?.shipTo_ContactPerson || 'N/A',
          'Contact Number': poHeader?.shipTo_ContactNo || 'N/A',
          'Remarks': poHeader?.remarks || 'N/A',
          'Destination': destinationInfo?.destinationName || 'N/A',
          'Destination Code': destinationInfo?.destinationCode || 'N/A',
        }
      ];

      const wsHeader = XLSX.utils.json_to_sheet(headerData);

      wsHeader['!cols'] = [
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 18 },
        { wch: 15 },
        { wch: 20 },
        { wch: 40 },
        { wch: 20 },
        { wch: 18 },
        { wch: 25 },
        { wch: 20 },
        { wch: 18 },
      ];

      wsHeader['!freeze'] = { ySplit: 1 };
      wsHeader['!printTitles'] = { rows: '1:1' };
      wsHeader['!pageSetup'] = commonPageSetup;
      wsHeader['!margins'] = commonMargins;
      wsHeader['!printArea'] = 'A1:M2';

      XLSX.utils.book_append_sheet(wb, wsHeader, 'PO Header');

      // ================= SHEET 2: ITEM DETAILS =================
      const itemsData = allItems.map(item => ({
        'PO Detail ID': item.poDetailId,
        'Item Description': item.itemDescription,
        'Quantity': item.qty,
        'UOM': item.uom,
        'Unit Price': Number(item.unitPrice).toFixed(2),
        'Total': (item.qty * item.unitPrice).toFixed(2),
        'Inventory Type': assetItems.some(a => a.poDetailId === item.poDetailId)
          ? 'Asset'
          : 'WMS',
      }));

      const wsItems = XLSX.utils.json_to_sheet(itemsData);

      wsItems['!cols'] = [
        { wch: 15 },
        { wch: 50 },
        { wch: 12 },
        { wch: 10 },
        { wch: 15 },
        { wch: 15 },
        { wch: 18 },
      ];

      wsItems['!freeze'] = { ySplit: 1 };
      wsItems['!printTitles'] = { rows: '1:1' };
      wsItems['!pageSetup'] = commonPageSetup;
      wsItems['!margins'] = commonMargins;
      wsItems['!printArea'] = `A1:G${itemsData.length + 1}`;

      XLSX.utils.book_append_sheet(wb, wsItems, 'Item Details');

      // ================= SHEET 3: SUMMARY =================
      const totalValue = allItems.reduce(
        (sum, i) => sum + i.qty * i.unitPrice,
        0
      );

      const wsSummary = XLSX.utils.aoa_to_sheet([
        ['Summary Information', ''],
        ['Total Items', allItems.length],
        ['Asset Items', assetItems.length],
        ['WMS Items', wmsItems.length],
        ['Total Value', totalValue.toFixed(2)],
      ]);

      wsSummary['!cols'] = [{ wch: 25 }, { wch: 20 }];
      wsSummary['!pageSetup'] = commonPageSetup;
      wsSummary['!margins'] = commonMargins;
      wsSummary['!printArea'] = 'A1:B5';

      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

      // ================= SAVE FILE =================
      const filename = `UsedInventory_${poHeader?.poNumber || 'PO'}_${new Date()
        .toISOString()
        .split('T')[0]}.xlsx`;

      XLSX.writeFile(wb, filename);

      Swal.fire({
        icon: 'success',
        title: 'Excel Generated!',
        text: 'Landscape | Table Header | Print-ready',
        timer: 1800,
        showConfirmButton: false,
      });

    } catch (error) {
      console.error('Excel Error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: error.message,
      });
    }
  };

  const toggleUnassignedSelection = (itemId) => {
    setSelectedUnassigned(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const toggleAssetSelection = (itemId) => {
    setSelectedAssetItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const toggleWMSSelection = (itemId) => {
    setSelectedWMSItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const forwardToAsset = () => {
    const itemsToMove = unassignedItems.filter(item => selectedUnassigned.includes(item.id));
    setAssetItems(prev => [...prev, ...itemsToMove]);
    setUnassignedItems(prev => prev.filter(item => !selectedUnassigned.includes(item.id)));
    setSelectedUnassigned([]);
  };

  const forwardToWMS = () => {
    const itemsToMove = unassignedItems.filter(item => selectedUnassigned.includes(item.id));
    setWMSItems(prev => [...prev, ...itemsToMove]);
    setUnassignedItems(prev => prev.filter(item => !selectedUnassigned.includes(item.id)));
    setSelectedUnassigned([]);
  };

  const moveBackFromAsset = () => {
    const itemsToMove = assetItems.filter(item => selectedAssetItems.includes(item.id));
    setUnassignedItems(prev => [...prev, ...itemsToMove]);
    setAssetItems(prev => prev.filter(item => !selectedAssetItems.includes(item.id)));
    setSelectedAssetItems([]);
  };

  const moveBackFromWMS = () => {
    const itemsToMove = wmsItems.filter(item => selectedWMSItems.includes(item.id));
    setUnassignedItems(prev => [...prev, ...itemsToMove]);
    setWMSItems(prev => prev.filter(item => !selectedWMSItems.includes(item.id)));
    setSelectedWMSItems([]);
  };

  const forwardAssetToWMS = () => {
    const itemsToMove = assetItems.filter(item => selectedAssetItems.includes(item.id));
    setWMSItems(prev => [...prev, ...itemsToMove]);
    setAssetItems(prev => prev.filter(item => !selectedAssetItems.includes(item.id)));
    setSelectedAssetItems([]);
  };

  const forwardWMSToAsset = () => {
    const itemsToMove = wmsItems.filter(item => selectedWMSItems.includes(item.id));
    setAssetItems(prev => [...prev, ...itemsToMove]);
    setWMSItems(prev => prev.filter(item => !selectedWMSItems.includes(item.id)));
    setSelectedWMSItems([]);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setSortBy('date-desc');
    setShipToFilter('');
  };

  const ItemCard = ({ item, selected, onToggle }) => (
    <div
      className={`p-4 mb-2 rounded-lg border-2 cursor-pointer transition-all ${selected
        ? 'border-blue-500 bg-blue-50'
        : 'border-gray-200 bg-white hover:border-blue-300'
        }`}
      onClick={onToggle}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="w-4 h-4 cursor-pointer mt-1"
          onClick={(e) => e.stopPropagation()}
        />
        <div className="flex-1">
          <p className="font-bold text-sm text-gray-800 mb-2">{item.itemDescription}</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-gray-500">PO Detail ID:</span>
              <span className="ml-1 font-semibold text-gray-700">{item.poDetailId}</span>
            </div>
            <div>
              <span className="text-gray-500">Quantity:</span>
              <span className="ml-1 font-semibold text-gray-700">{item.qty} {item.uom}</span>
            </div>
            <div>
              <span className="text-gray-500">Unit Price:</span>
              <span className="ml-1 font-semibold text-green-600">₱{item.unitPrice.toFixed(2)}</span>
            </div>
            <div>
              <span className="text-gray-500">Total:</span>
              <span className="ml-1 font-bold text-blue-600">₱{(item.qty * item.unitPrice).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const uniqueShipToLocations = [...new Set(shippingLocations)];

  const filteredPOs = poList
    .filter(po => {
      const matchesSearch = po.poNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.inventoryDestinationPRId?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesShipTo = !shipToFilter || po.shipTo_Location === shipToFilter;

      if (!matchesSearch || !matchesShipTo) return false;

      if (startDate || endDate) {
        const poDate = new Date(po.poCreatedDate);
        if (startDate && poDate < new Date(startDate)) return false;
        if (endDate && poDate > new Date(endDate + 'T23:59:59')) return false;
      }

      const matchingPR = prHeaders.find(pr => pr.prNo === po.inventoryDestinationPRId);
      if (!matchingPR) return false;

      const destination = destinations.find(d => d.destinationId === matchingPR.inventoryDestinationId);
      if (!destination) return false;

      return destination.view === true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.poCreatedDate) - new Date(a.poCreatedDate);
        case 'date-asc':
          return new Date(a.poCreatedDate) - new Date(b.poCreatedDate);
        case 'po-asc':
          return (a.poNumber || '').localeCompare(b.poNumber || '');
        case 'po-desc':
          return (b.poNumber || '').localeCompare(a.poNumber || '');
        default:
          return 0;
      }
    });

  if (!selectedPO) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Package className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800"> Purchase Order Inventory Distribution
            </h1>
          </div>
          <p className="text-gray-600">Choose a PO to distribute inventory</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search PO Number or PR..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>

          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-5 h-5 text-blue-600" />
              <h3 className="font-bold text-gray-800">Filters & Sort</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Ship To Location</label>
                <select
                  value={shipToFilter}
                  onChange={(e) => setShipToFilter(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm cursor-pointer"
                >
                  <option value="">All Locations</option>
                  {uniqueShipToLocations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Sort By</label>
                <div className="relative">
                  <ArrowUpDown className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm appearance-none bg-white cursor-pointer"
                  >
                    <option value="date-desc">Date (Newest First)</option>
                    <option value="date-asc">Date (Oldest First)</option>
                    <option value="po-asc">PO Number (A-Z)</option>
                    <option value="po-desc">PO Number (Z-A)</option>
                  </select>
                </div>
              </div>

              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all font-semibold text-sm flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Clear All
                </button>
              </div>
            </div>

            {(searchTerm || startDate || endDate || shipToFilter || sortBy !== 'date-desc') && (
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="text-xs font-semibold text-gray-600">Active filters:</span>
                {searchTerm && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                    Search: "{searchTerm}"
                  </span>
                )}
                {startDate && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                    From: {new Date(startDate).toLocaleDateString()}
                  </span>
                )}
                {endDate && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                    To: {new Date(endDate).toLocaleDateString()}
                  </span>
                )}
                {shipToFilter && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-semibold">
                    Location: {shipToFilter}
                  </span>
                )}
                {sortBy !== 'date-desc' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold">
                    Sort: {sortBy === 'date-asc' ? 'Date ↑' : sortBy === 'po-asc' ? 'PO ↑' : 'PO ↓'}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="mb-4 text-sm text-gray-600 font-semibold">
            Showing <span className="text-blue-600 font-bold">{filteredPOs.length}</span> result{filteredPOs.length !== 1 ? 's' : ''}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p>Loading POs...</p>
            </div>
          ) : filteredPOs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="font-semibold text-lg mb-2">No POs found</p>
              <p className="text-sm">Try adjusting your filters or search terms</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-lg overflow-hidden shadow-lg">
                <thead className="bg-blue-600 text-white">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold">PO Number</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">PR Reference</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Date Created</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Ship To</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Status</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Receiving Status</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredPOs.map((po, index) => (
                    <tr
                      key={po.id}
                      className={`hover:bg-blue-50 transition-colors cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}
                      onClick={() => handleSelectPO(po)}
                    >
                      <td className="px-6 py-4">
                        <span className="font-bold text-blue-600">{po.poNumber}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {po.inventoryDestinationPRId || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(po.poCreatedDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {po.shipTo_Location}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-block px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                          Approved
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">
                          {po.receivingStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectPO(po);
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm font-semibold"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  const isAssetOnly = destinationInfo?.asset && !destinationInfo?.wms && !destinationInfo?.both;
  const isWMSOnly = destinationInfo?.wms && !destinationInfo?.asset && !destinationInfo?.both;
  const isBoth = destinationInfo?.both;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      {loading && (
        <div className="fixed top-4 right-4 bg-yellow-500 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          Loading...
        </div>
      )}
      <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <Package className="w-8 h-8 text-blue-600" />
              <h1 className="text-3xl font-bold text-gray-800">Inventory Distribution</h1>
            </div>

            {/* Main PO Info */}
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4">
              <div>
                <p className="text-gray-600">
                  PO Number: <span className="font-bold text-blue-600">{poHeader?.poNumber}</span>
                </p>
                <p className="text-sm text-gray-500">
                  Status: <span className="font-semibold">{poHeader?.status}</span>
                </p>
                <p className="text-sm text-gray-500">
                  PR Reference: {poHeader?.inventoryDestinationPRId || poHeader?.referencePRId || 'N/A'}
                </p>
                <p className="text-sm text-gray-500">
                  PO Created: {poHeader?.poCreatedDate ? new Date(poHeader.poCreatedDate).toLocaleDateString() : 'N/A'}
                </p>
                <p className="text-sm text-gray-500">
                  Delivery Date: {poHeader?.deliveryDate ? new Date(poHeader.deliveryDate).toLocaleDateString() : 'N/A'}
                </p>
                <p className="text-sm text-gray-500">
                  Prepared By: {poHeader?.preparedBy || 'N/A'}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">
                  Company ID: {poHeader?.companyId || 'N/A'}
                </p>
                <p className="text-sm text-gray-500">
                  Vendor ID: {poHeader?.vendorId || 'N/A'}
                </p>
                <p className="text-sm text-gray-500">
                  Department ID: {poHeader?.departmentId || 'N/A'}
                </p>
                <p className="text-sm text-gray-500">
                  Location ID: {poHeader?.locationId || 'N/A'}
                </p>
                <p className="text-sm text-gray-500">
                  Payment Terms: {poHeader?.paymentTerms || 'N/A'}
                </p>
                <p className="text-sm text-gray-500">
                  Receiving Status: {poHeader?.receivingStatus || 'N/A'}
                </p>
              </div>
            </div>

            {/* Shipping Information */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-xs font-bold text-gray-700 mb-2">SHIPPING INFORMATION</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                <div>
                  <p className="text-sm text-gray-600">
                    Location: <span className="font-semibold">{poHeader?.shipTo_Location || 'N/A'}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Address 1: {poHeader?.shipTo_Address1 || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Address 2: {poHeader?.shipTo_Address2 || '-'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Address 3: {poHeader?.shipTo_Address3 || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    Contact Person: {poHeader?.shipTo_ContactPerson || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Contact No: {poHeader?.shipTo_ContactNo || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-blue-50 rounded-lg p-2">
                <p className="text-xs text-gray-600">VAT Inclusive</p>
                <p className="text-sm font-bold text-blue-700">
                  {poHeader?.isVatInclusive ? 'Yes' : 'No'}
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-2">
                <p className="text-xs text-gray-600">Non-VAT</p>
                <p className="text-sm font-bold text-blue-700">
                  {poHeader?.isNonVAT ? 'Yes' : 'No'}
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-2">
                <p className="text-xs text-gray-600">Header Discount</p>
                <p className="text-sm font-bold text-blue-700">
                  {poHeader?.headerDiscount ? `${poHeader.headerDiscount}%` : '0%'}
                </p>
              </div>
            </div>

            {/* Printing & Status Info */}
            <div className="flex gap-4 mb-4">
              <div className="text-xs text-gray-500">
                PO Printed: <span className="font-semibold">{poHeader?.isPOPrinted ? 'Yes' : 'No'}</span>
              </div>
              <div className="text-xs text-gray-500">
                Reprint Count: <span className="font-semibold">{poHeader?.reprintCount || 0}</span>
              </div>
              <div className="text-xs text-gray-500">
                From Quotation: <span className="font-semibold">{poHeader?.isFromQuotation ? 'Yes' : 'No'}</span>
              </div>
              {poHeader?.rate && (
                <div className="text-xs text-gray-500">
                  Rate: <span className="font-semibold">{poHeader.rate}</span>
                </div>
              )}
            </div>

            {/* Remarks */}
            {poHeader?.remarks && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
                <p className="text-xs font-bold text-yellow-800 mb-1">REMARKS</p>
                <p className="text-sm text-yellow-900">{poHeader.remarks}</p>
              </div>
            )}

            {/* Destination Info */}
            {destinationInfo && (
              <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-bold text-blue-800">
                    Destination: {destinationInfo.destinationName} ({destinationInfo.destinationCode})
                  </p>
                  <p className="text-xs text-blue-600">
                    {isAssetOnly && "🏢 Asset Only - Auto-assigned"}
                    {isWMSOnly && "📦 WMS Only - Auto-assigned"}
                    {isBoth && "🔄 Both - Select and forward items"}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 ml-6">
            <button
              onClick={handlePrintToUsedInventory}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold relative"
            >
              <FileSpreadsheet className="w-5 h-5" />
              <span>Print to Used Inventory</span>
              {(assetItems.length > 0 || wmsItems.length > 0) && (
                <span className="absolute -top-2 -right-2 px-2 py-1 bg-red-500 text-white rounded-full text-xs font-bold border-2 border-white shadow-lg">
                  {assetItems.length + wmsItems.length}
                </span>
              )}
            </button>
            <button
              onClick={() => {
                setSelectedPO(null);
                setUnassignedItems([]);
                setAssetItems([]);
                setWMSItems([]);
                setDestinationInfo(null);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to PO List
            </button>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <div className="mb-4 text-center">
          <p className="text-sm text-gray-600">
            💡 <span className="font-semibold">Tip:</span> Click on items to select them, then use forward buttons to assign
          </p>
        </div>

        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-blue-50 rounded-lg border-2 border-blue-200">
          <div className="flex items-center gap-2 mb-3">
            <Building className="w-5 h-5 text-blue-600" />
            <h3 className="font-bold text-gray-800">Update Shipping Location</h3>
          </div>
          <div className="flex gap-3">
            <select
              onChange={(e) => handleLocationSelect(e.target.value)}
              disabled={isUpdatingLocation}
              value=""
              className="flex-1 px-4 py-3 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Select new location...</option>
              {uniqueShipToLocations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
            {isUpdatingLocation && (
              <div className="flex items-center gap-2 px-4 py-3 bg-orange-100 rounded-lg">
                <div className="animate-spin w-5 h-5 border-3 border-orange-600 border-t-transparent rounded-full"></div>
                <span className="text-sm font-semibold text-orange-700">Updating...</span>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-600 mt-2">
            Current: <span className="font-bold text-orange-600">{poHeader?.shipTo_Location}</span>
          </p>
        </div>

        <div className="space-y-6">
          {isBoth && (
            <div className="w-full">
              <div className="rounded-lg p-4 border-2 border-gray-300 bg-gray-50">
                <h3 className="font-bold text-xl mb-4 flex items-center gap-2 text-gray-700">
                  <Package className="w-7 h-7" />
                  Unassigned ({unassignedItems.length})
                </h3>

                <div className="space-y-2 min-h-[400px] max-h-[400px] overflow-y-auto pr-2 mb-4">
                  {unassignedItems.map(item => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      selected={selectedUnassigned.includes(item.id)}
                      onToggle={() => toggleUnassignedSelection(item.id)}
                    />
                  ))}
                  {unassignedItems.length === 0 && (
                    <div className="text-center text-gray-400 py-20">
                      <Package className="w-20 h-20 mx-auto mb-3 opacity-50" />
                      <p className="font-semibold text-lg">All items assigned</p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={forwardToAsset}
                    disabled={selectedUnassigned.length === 0}
                    className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 font-bold shadow-lg"
                  >
                    <ArrowRight className="w-5 h-5" />
                    To Asset ({selectedUnassigned.length})
                  </button>
                  <button
                    onClick={forwardToWMS}
                    disabled={selectedUnassigned.length === 0}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 font-bold shadow-lg"
                  >
                    <ArrowRight className="w-5 h-5" />
                    To WMS ({selectedUnassigned.length})
                  </button>
                </div>
              </div>
            </div>
          )}

          {destinationInfo?.asset && (
            <div className="w-full">
              <div className={`rounded-lg p-4 border-2 ${isAssetOnly
                ? 'bg-green-100 border-green-400'
                : 'bg-green-50 border-green-300'
                }`}>
                <h3 className="font-bold text-xl mb-4 flex items-center gap-2 text-green-700">
                  <Building className="w-7 h-7" />
                  Asset Inventory ({assetItems.length})
                  {isAssetOnly && <span className="text-xs bg-green-200 px-2 py-1 rounded">AUTO</span>}
                </h3>

                <div className="space-y-2 min-h-[400px] max-h-[400px] overflow-y-auto pr-2 mb-4">
                  {assetItems.map(item => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      selected={selectedAssetItems.includes(item.id)}
                      onToggle={() => toggleAssetSelection(item.id)}
                    />
                  ))}
                  {assetItems.length === 0 && (
                    <div className="text-center text-green-400 py-20">
                      <Building className="w-20 h-20 mx-auto mb-3 opacity-50" />
                      <p className="font-semibold text-lg">No items in Asset</p>
                    </div>
                  )}
                </div>

                {isBoth && (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={moveBackFromAsset}
                      disabled={selectedAssetItems.length === 0}
                      className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 font-semibold"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      Back to Unassigned ({selectedAssetItems.length})
                    </button>
                    <button
                      onClick={forwardAssetToWMS}
                      disabled={selectedAssetItems.length === 0}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 font-bold shadow-lg"
                    >
                      <ArrowRight className="w-5 h-5" />
                      To WMS ({selectedAssetItems.length})
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {destinationInfo?.wms && (
            <div className="w-full">
              <div className={`rounded-lg p-4 border-2 ${isWMSOnly
                ? 'bg-blue-100 border-blue-400'
                : 'bg-blue-50 border-blue-300'
                }`}>
                <h3 className="font-bold text-xl mb-4 flex items-center gap-2 text-blue-700">
                  <Warehouse className="w-7 h-7" />
                  WMS Inventory ({wmsItems.length})
                  {isWMSOnly && <span className="text-xs bg-blue-200 px-2 py-1 rounded">AUTO</span>}
                </h3>

                <div className="space-y-2 min-h-[400px] max-h-[400px] overflow-y-auto pr-2 mb-4">
                  {wmsItems.map(item => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      selected={selectedWMSItems.includes(item.id)}
                      onToggle={() => toggleWMSSelection(item.id)}
                    />
                  ))}
                  {wmsItems.length === 0 && (
                    <div className="text-center text-blue-400 py-20">
                      <Warehouse className="w-20 h-20 mx-auto mb-3 opacity-50" />
                      <p className="font-semibold text-lg">No items in WMS</p>
                    </div>
                  )}
                </div>

                {isBoth && (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={moveBackFromWMS}
                      disabled={selectedWMSItems.length === 0}
                      className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 font-semibold"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      Back to Unassigned ({selectedWMSItems.length})
                    </button>
                    <button
                      onClick={forwardWMSToAsset}
                      disabled={selectedWMSItems.length === 0}
                      className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 font-bold shadow-lg"
                    >
                      <ArrowRight className="w-5 h-5" />
                      To Asset ({selectedWMSItems.length})
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border-2 border-blue-200">
          <div className="flex justify-between items-center">
            <div className="flex gap-6 text-sm font-semibold">
              {isBoth && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                  <span>Unassigned: {unassignedItems.length}</span>
                </div>
              )}
              {destinationInfo?.asset && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span>Asset: {assetItems.length}</span>
                </div>
              )}
              {destinationInfo?.wms && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span>WMS: {wmsItems.length}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span>Total Selected: {selectedUnassigned.length + selectedAssetItems.length + selectedWMSItems.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryDistribution;